/**
 * ClientAlerts — Transfer vs New Active Client decision panel
 * Accessible to Admin/Controller only.
 * Shows pending alerts and lets Mr. B resolve each one.
 */
import { useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";

const PRACTICE_LABELS: Record<string, string> = {
  apx: "APX",
  "apx-hrb": "APX-HRB",
  "apx-bbn": "APX-BBN",
};

const STATUS_COLORS: Record<string, string> = {
  pending: "oklch(0.82 0.01 240)",
  transfer: "oklch(0.82 0.01 240)",
  new_active: "oklch(0.55 0.01 240)",
  dismissed: "oklch(0.45 0.02 240)",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  transfer: "Transfer",
  new_active: "New Active",
  dismissed: "Dismissed",
};

type FilterStatus = "pending" | "all";

export default function ClientAlerts() {
  const { user } = useAuth();
  const [filter, setFilter] = useState<FilterStatus>("pending");
  const [noteInputs, setNoteInputs] = useState<Record<number, string>>({});
  const [createForm, setCreateForm] = useState({ open: false, clientName: "", clientEmail: "", practiceKey: "", note: "" });

  const { data: alerts = [], refetch, isLoading } = trpc.clientAlerts.list.useQuery({ status: filter });
  const { data: pendingCount = 0 } = trpc.clientAlerts.pendingCount.useQuery();

  const resolveMutation = trpc.clientAlerts.resolve.useMutation({
    onSuccess: () => {
      refetch();
      toast.success("Alert resolved");
    },
    onError: (e) => toast.error(e.message),
  });

  const createMutation = trpc.clientAlerts.create.useMutation({
    onSuccess: () => {
      refetch();
      setCreateForm({ open: false, clientName: "", clientEmail: "", practiceKey: "", note: "" });
      toast.success("Alert created");
    },
    onError: (e) => toast.error(e.message),
  });

  function resolve(id: number, resolution: "transfer" | "new_active" | "dismissed") {
    resolveMutation.mutate({
      id,
      resolution,
      resolvedBy: user?.email ?? "unknown",
      note: noteInputs[id] || undefined,
    });
  }

  const s = {
    page: {
      padding: "32px 28px",
      maxWidth: 860,
      margin: "0 auto",
    } as React.CSSProperties,
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 24,
    } as React.CSSProperties,
    title: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      fontSize: 20,
      fontWeight: 700,
      color: "oklch(0.18 0.02 240)",
    } as React.CSSProperties,
    badge: {
      background: "oklch(0.82 0.01 240)",
      color: "oklch(0.12 0.04 240)",
      borderRadius: 12,
      padding: "2px 9px",
      fontSize: 12,
      fontWeight: 700,
    } as React.CSSProperties,
    filterRow: {
      display: "flex",
      gap: 8,
      marginBottom: 20,
    } as React.CSSProperties,
    filterBtn: (active: boolean) => ({
      padding: "6px 16px",
      borderRadius: 8,
      border: "1px solid",
      borderColor: active ? "oklch(0.82 0.01 240)" : "oklch(0.3 0.04 240)",
      background: active ? "oklch(0.92 0.005 240 / 15%)" : "transparent",
      color: active ? "oklch(0.08 0.01 240)" : "oklch(0.08 0.01 240)",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
    } as React.CSSProperties),
    card: {
      background: "oklch(0.99 0.002 85)",
      border: "1px solid oklch(0.88 0.01 240)",
      borderRadius: 12,
      padding: "18px 20px",
      marginBottom: 12,
    } as React.CSSProperties,
    clientName: {
      fontSize: 16,
      fontWeight: 700,
      color: "oklch(0.18 0.02 240)",
      marginBottom: 4,
    } as React.CSSProperties,
    meta: {
      fontSize: 12,
      color: "oklch(0.14 0.04 240)",
      marginBottom: 12,
      display: "flex",
      gap: 12,
      flexWrap: "wrap" as const,
    } as React.CSSProperties,
    statusPill: (status: string) => ({
      display: "inline-block",
      padding: "2px 10px",
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 700,
      background: `${STATUS_COLORS[status] ?? "oklch(0.4 0.02 240)"} / 20%`,
      color: "oklch(0.08 0.01 240)",
      border: `1px solid ${STATUS_COLORS[status] ?? "oklch(0.4 0.02 240)"} / 40%`,
    } as React.CSSProperties),
    noteInput: {
      width: "100%",
      background: "oklch(0.94 0.018 220)",
      border: "1px solid oklch(0.3 0.04 240)",
      borderRadius: 8,
      padding: "8px 12px",
      color: "oklch(0.20 0.02 240)",
      fontSize: 13,
      marginBottom: 12,
      boxSizing: "border-box" as const,
    } as React.CSSProperties,
    actionRow: {
      display: "flex",
      gap: 8,
    } as React.CSSProperties,
    btn: (variant: "transfer" | "new" | "dismiss") => {
      const colors = {
        transfer: { bg: "oklch(0.92 0.005 240 / 15%)", border: "oklch(0.92 0.005 240 / 50%)", text: "oklch(0.82 0.01 240)" },
        new: { bg: "oklch(0.92 0.005 240 / 15%)", border: "oklch(0.92 0.005 240 / 50%)", text: "oklch(0.55 0.01 240)" },
        dismiss: { bg: "transparent", border: "oklch(0.14 0.04 240)", text: "oklch(0.5 0.02 240)" },
      };
      const c = colors[variant];
      return {
        padding: "7px 16px",
        borderRadius: 8,
        border: `1px solid ${c.border}`,
        background: c.bg,
        color: c.text,
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
      } as React.CSSProperties;
    },
    emptyState: {
      textAlign: "center" as const,
      padding: "48px 24px",
      color: "oklch(0.14 0.04 240)",
      fontSize: 14,
    } as React.CSSProperties,
    createBtn: {
      padding: "8px 18px",
      borderRadius: 8,
      border: "1px solid oklch(0.92 0.005 240 / 50%)",
      background: "oklch(0.92 0.005 240 / 12%)",
      color: "oklch(0.08 0.01 240)",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
    } as React.CSSProperties,
    modal: {
      position: "fixed" as const,
      inset: 0,
      background: "oklch(0.08 0.04 240 / 80%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 50,
    } as React.CSSProperties,
    modalCard: {
      background: "oklch(0.93 0.008 240)",
      border: "1px solid oklch(0.3 0.04 240)",
      borderRadius: 16,
      padding: 28,
      width: 420,
      maxWidth: "90vw",
    } as React.CSSProperties,
    label: {
      display: "block",
      fontSize: 12,
      fontWeight: 600,
      color: "oklch(0.14 0.04 240)",
      marginBottom: 4,
      marginTop: 12,
    } as React.CSSProperties,
    input: {
      width: "100%",
      background: "oklch(0.94 0.018 220)",
      border: "1px solid oklch(0.3 0.04 240)",
      borderRadius: 8,
      padding: "8px 12px",
      color: "oklch(0.20 0.02 240)",
      fontSize: 13,
      boxSizing: "border-box" as const,
    } as React.CSSProperties,
  };

  return (
    <DashboardLayout>
      <div style={s.page}>
        {/* Header */}
        <div style={s.header}>
          <div style={s.title}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="oklch(0.82 0.01 240)" strokeWidth="1.8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            Client Alerts
            {pendingCount > 0 && <span style={s.badge}>{pendingCount}</span>}
          </div>
          <button style={s.createBtn} onClick={() => setCreateForm(f => ({ ...f, open: true }))}>
            + New Alert
          </button>
        </div>

        {/* Filter tabs */}
        <div style={s.filterRow}>
          <button style={s.filterBtn(filter === "pending")} onClick={() => setFilter("pending")}>
            Pending {pendingCount > 0 ? `(${pendingCount})` : ""}
          </button>
          <button style={s.filterBtn(filter === "all")} onClick={() => setFilter("all")}>
            All Alerts
          </button>
        </div>

        {/* Alert list */}
        {isLoading ? (
          <div style={s.emptyState}>Loading…</div>
        ) : alerts.length === 0 ? (
          <div style={s.emptyState}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="oklch(0.14 0.04 240)" strokeWidth="1.2" style={{ margin: "0 auto 12px", display: "block" }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            No {filter === "pending" ? "pending " : ""}alerts
          </div>
        ) : (
          alerts.map((alert) => (
            <div key={alert.id} style={s.card}>
              <div style={s.clientName}>{alert.clientName}</div>
              <div style={s.meta}>
                <span>Source: <strong>{alert.source === 'xero_sync' ? '🔄 Xero Sync' : alert.source === 'zapier' ? '⚡ Zapier' : 'Manual'}</strong></span>
                {alert.practiceKey && <span>Practice: <strong>{PRACTICE_LABELS[alert.practiceKey] ?? alert.practiceKey}</strong></span>}
                {alert.clientEmail && <span>{alert.clientEmail}</span>}
                <span style={s.statusPill(alert.status)}>{STATUS_LABELS[alert.status] ?? alert.status}</span>
                <span>{new Date(alert.createdAt).toLocaleDateString()}</span>
              </div>
              {alert.note && (
                <div style={{ fontSize: 13, color: "oklch(0.14 0.04 240)", marginBottom: 10, fontStyle: "italic" }}>
                  {alert.note}
                </div>
              )}
              {alert.status === "pending" && (
                <>
                  <input
                    style={s.noteInput}
                    placeholder="Optional note (e.g. 'Moving from APX to APX-BBN')"
                    value={noteInputs[alert.id] ?? ""}
                    onChange={(e) => setNoteInputs(n => ({ ...n, [alert.id]: e.target.value }))}
                  />
                  <div style={s.actionRow}>
                    <button style={s.btn("transfer")} onClick={() => resolve(alert.id, "transfer")}>
                      Transfer Client
                    </button>
                    <button style={s.btn("new")} onClick={() => resolve(alert.id, "new_active")}>
                      New Active Client
                    </button>
                    <button style={s.btn("dismiss")} onClick={() => resolve(alert.id, "dismissed")}>
                      Dismiss
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: "oklch(0.28 0.03 240)", marginTop: 8 }}>
                    <strong>Transfer</strong> = client moves between practices (no net gain) &nbsp;·&nbsp;
                    <strong>New Active</strong> = genuinely new client (adds to count)
                  </div>
                </>
              )}
              {alert.status !== "pending" && alert.resolvedBy && (
                <div style={{ fontSize: 11, color: "oklch(0.28 0.03 240)", marginTop: 4 }}>
                  Resolved by {alert.resolvedBy} · {alert.resolvedAt ? new Date(alert.resolvedAt).toLocaleDateString() : ""}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create alert modal */}
      {createForm.open && (
        <div style={s.modal} onClick={() => setCreateForm(f => ({ ...f, open: false }))}>
          <div style={s.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "oklch(0.18 0.02 240)", marginBottom: 16 }}>
              Create Client Alert
            </div>
            <label style={s.label}>Client Name *</label>
            <input
              style={s.input}
              value={createForm.clientName}
              onChange={(e) => setCreateForm(f => ({ ...f, clientName: e.target.value }))}
              placeholder="e.g. Taco Palace LLC"
            />
            <label style={s.label}>Client Email</label>
            <input
              style={s.input}
              value={createForm.clientEmail}
              onChange={(e) => setCreateForm(f => ({ ...f, clientEmail: e.target.value }))}
              placeholder="client@example.com"
            />
            <label style={s.label}>Practice</label>
            <select
              style={s.input}
              value={createForm.practiceKey}
              onChange={(e) => setCreateForm(f => ({ ...f, practiceKey: e.target.value }))}
            >
              <option value="">— Select practice —</option>
              <option value="apx">APX</option>
              <option value="apx-hrb">APX-HRB</option>
              <option value="apx-bbn">APX-BBN</option>
            </select>
            <label style={s.label}>Note</label>
            <input
              style={s.input}
              value={createForm.note}
              onChange={(e) => setCreateForm(f => ({ ...f, note: e.target.value }))}
              placeholder="Optional context"
            />
            <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
              <button
                style={{ ...s.btn("dismiss"), padding: "8px 18px" }}
                onClick={() => setCreateForm(f => ({ ...f, open: false }))}
              >
                Cancel
              </button>
              <button
                style={{ ...s.createBtn, padding: "8px 18px" }}
                onClick={() => {
                  if (!createForm.clientName.trim()) { toast.error("Client name is required"); return; }
                  createMutation.mutate({
                    clientName: createForm.clientName.trim(),
                    clientEmail: createForm.clientEmail.trim() || undefined,
                    practiceKey: createForm.practiceKey || undefined,
                    note: createForm.note.trim() || undefined,
                    source: "manual",
                  });
                }}
              >
                Create Alert
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
